/**
 * Textarea with Word-style red squiggles under misspelled words and corrections on
 * right-click. Underlines are drawn by a transparent mirror layered over the field.
 *
 * On touch devices, where there is no right-click, tapping a squiggled word opens
 * the same suggestions as a popover anchored over the word.
 */

import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { Input } from "antd";
import { replaceRange } from "../spellCheck";
import useMirrorGeometry from "./useMirrorGeometry";
import usePointerCapabilities from "./usePointerCapabilities";
import useSuggestionMenu from "./useSuggestionMenu";
import styles from "./SpellCheckTextArea.module.scss";

const { TextArea } = Input;

function hitTestMirror(mirror, ranges, x, y) {
  if (!mirror) return null;

  for (const mark of mirror.querySelectorAll("[data-range-index]")) {
    for (const rect of mark.getClientRects()) {
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        const range = ranges[Number(mark.dataset.rangeIndex)];
        if (!range) return null;

        return {
          range,
          rect: {
            left: rect.left,
            top: rect.top,
            bottom: rect.bottom,
            width: rect.right - rect.left,
          },
        };
      }
    }
  }

  return null;
}

function toMirrorSegments(value, ranges) {
  const segments = [];
  let cursor = 0;

  ranges.forEach((range, index) => {
    if (range.start > cursor) segments.push(value.slice(cursor, range.start));
    segments.push(
      <span
        key={`${range.start}-${range.end}`}
        data-range-index={index}
        className={styles.error}
      >
        {value.slice(range.start, range.end)}
      </span>
    );
    cursor = range.end;
  });

  segments.push(value.slice(cursor));
  if (value.endsWith("\n")) segments.push("\n");

  return segments;
}

const SpellCheckTextArea = ({ value, onChange, ranges, ...textAreaProps }) => {
  const { wrapperRef, mirrorRef, antdRef, getTextArea, syncScroll } = useMirrorGeometry(
    value,
    ranges
  );
  const { menu, menuRef, openAtPoint, openOverWord, close } = useSuggestionMenu();
  const { isCoarsePointer } = usePointerCapabilities();

  const handleContextMenu = (event) => {
    const hit = hitTestMirror(mirrorRef.current, ranges, event.clientX, event.clientY);
    if (!hit) {
      close();
      return;
    }

    event.preventDefault();
    openAtPoint(event.clientX, event.clientY, hit.range);
  };

  const handleClick = (event) => {
    if (!isCoarsePointer) return;

    const hit = hitTestMirror(mirrorRef.current, ranges, event.clientX, event.clientY);
    if (!hit) {
      close();
      return;
    }

    openOverWord(hit.rect, hit.range);
  };

  const handleScroll = () => {
    syncScroll();
    close();
  };

  const applySuggestion = (suggestion) => {
    const { range } = menu;
    onChange(replaceRange(value, range.start, range.end, suggestion));
    close();
    getTextArea()?.focus();
  };

  return (
    <div
      ref={wrapperRef}
      className={styles.wrapper}
      onContextMenu={handleContextMenu}
      onClick={handleClick}
    >
      <TextArea
        {...textAreaProps}
        ref={antdRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onScroll={handleScroll}
        spellCheck={false}
      />

      <div ref={mirrorRef} className={styles.mirror} aria-hidden="true">
        {toMirrorSegments(value, ranges)}
      </div>

      {menu &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            data-testid="sms-spellcheck-menu"
            style={{ left: menu.x, top: menu.y }}
          >
            {menu.range.suggestions.length > 0 ? (
              menu.range.suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  onClick={() => applySuggestion(suggestion)}
                >
                  {suggestion}
                </button>
              ))
            ) : (
              <span className={styles.menuEmpty}>No suggestions</span>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

SpellCheckTextArea.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ranges: PropTypes.arrayOf(
    PropTypes.shape({
      word: PropTypes.string.isRequired,
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
      suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
};

export default SpellCheckTextArea;
